import { z } from "zod"

import { documentTypeKeys } from "../../primitives.js"
import { selectableDocumentTypeKeys } from "../../registries.js"

export const ApiUploadPartRequestSchema = z.object({
  partIndex: z.number().int().min(0),
  pageStart: z.number().int().min(1).optional(),
  pageEnd: z.number().int().min(1).optional(),
  byteSize: z.number().int().positive().optional(),
  contentHash: z
    .string()
    .regex(/^(?:sha256:)?[0-9a-fA-F]{64}$/)
    .optional(),
})

export type ApiUploadPartRequest = z.infer<typeof ApiUploadPartRequestSchema>

export const ApiUploadRequestSchema = z.object({
  originalFileName: z.string().min(1),
  mimeType: z.string().min(1),
  byteSize: z.number().int().positive(),
  contentHash: z.string().regex(/^(?:sha256:)?[0-9a-fA-F]{64}$/),
  clientFileId: z.string().optional(),
  parts: z.array(ApiUploadPartRequestSchema).optional(),
})

export type ApiUploadRequest = z.infer<typeof ApiUploadRequestSchema>

export const ApiCreateBatchRequestSchema = z.object({
  idempotencyKey: z.string().min(1).optional(),
  externalReference: z.string().optional(),
  uploads: z.array(ApiUploadRequestSchema).min(1),
})

export type ApiCreateBatchRequest = z.infer<typeof ApiCreateBatchRequestSchema>

export const ApiBatchUploadPartResponseSchema = z.object({
  uploadPartId: z.string().uuid(),
  partIndex: z.number().int(),
})

export type ApiBatchUploadPartResponse = z.infer<
  typeof ApiBatchUploadPartResponseSchema
>

export const ApiBatchUploadResponseSchema = z.object({
  uploadId: z.string().uuid(),
  sourceArtifactId: z.string().uuid(),
  blobContainer: z.string(),
  blobPath: z.string(),
  uploadUrl: z.string().url(),
  uploadUrlExpiresAt: z.string().datetime(),
  uploadHeaders: z.record(z.string(), z.string()),
  queueMessageId: z.string().nullable(),
  parts: z.array(ApiBatchUploadPartResponseSchema),
})

export type ApiBatchUploadResponse = z.infer<
  typeof ApiBatchUploadResponseSchema
>

export const ApiCreateBatchResponseSchema = z.object({
  batchId: z.string().uuid(),
  organizationId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  idempotencyKey: z.string(),
  idempotentReplay: z.boolean(),
  uploads: z.array(ApiBatchUploadResponseSchema),
})

export type ApiCreateBatchResponse = z.infer<
  typeof ApiCreateBatchResponseSchema
>

export const ApiCompleteUploadRequestSchema = z.object({
  idempotencyKey: z.string().min(1).optional(),
  // Only types the active processor can extract are selectable on upload.
  // Omitted means the worker classifier chooses the extractor.
  documentTypeKey: z.enum(selectableDocumentTypeKeys).optional(),
})

export type ApiCompleteUploadRequest = z.infer<
  typeof ApiCompleteUploadRequestSchema
>

export const ApiCompleteUploadResponseSchema = z.object({
  batchId: z.string().uuid(),
  uploadId: z.string().uuid(),
  sourceArtifactId: z.string().uuid(),
  documentId: z.string().uuid(),
  processingRunId: z.string().uuid(),
  runAttemptId: z.string().uuid(),
  documentTypeKey: z.enum(documentTypeKeys),
  processorVersionKey: z.string(),
  queueMessageId: z.string(),
  idempotentReplay: z.boolean(),
})

export type ApiCompleteUploadResponse = z.infer<
  typeof ApiCompleteUploadResponseSchema
>
